import styled, { css } from 'styled-components';
import React from 'react';
import {focusRing, primitiveColors, radius} from '../../../../styles/template';

interface PropsType {
    children: React.ReactNode
    onClick?: React.MouseEventHandler<HTMLElement>
    large?: boolean
    primary?: boolean
    disabled?: boolean
    transparent?: boolean
    className?: string
    type?: 'button' | 'submit' | 'reset'
    success?: boolean
    href?: string
    faint?: boolean
    /**
     * Опасное/деструктивное действие — красный фон, белая иконка.
     * Используется, например, для кнопки удаления в swipe-жесте
     * `RemovableItem` / `TestListItem`
     * (см. `SwipeAction` в `RemovableItem/removable.shared.ts`).
     */
    danger?: boolean
    'aria-label'?: string
    /** Состояние toggle-кнопки (например «в избранном»). Прокидывается на <button>. */
    'aria-pressed'?: boolean
}

const ICON_BUTTON_STYLE_PROPS = ['large', 'primary', 'transparent', 'success', 'faint', 'danger'] as const;

const Wrap = styled.button.withConfig({
    shouldForwardProp: (prop) => !ICON_BUTTON_STYLE_PROPS.includes(prop as (typeof ICON_BUTTON_STYLE_PROPS)[number]),
})<PropsType>`
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: ${(p) => p.theme.components?.Button?.radius ?? p.theme.primitives?.radius?.sm ?? radius.normal};

    height: 5rem;
    width: 5rem;
    background: ${primitiveColors.alpha.black25};
    transition: all 0.2s ease-out;

    /* Иконка наследует белый по умолчанию */
    color: ${primitiveColors.gray[50]};

    &:active {
        transform: translateY(0.5rem);
    }

    &:hover {
        opacity: 0.8;
    }

    ${focusRing}

    ${({ large }) => large && css`
        height: 6rem;
        width: 6rem;
    `}

    ${({ primary, theme }) => primary && css`
        background: ${theme.brand?.primary ?? primitiveColors.primary[500]};
        &:hover {
            background: ${theme.brand?.primaryLight ?? primitiveColors.primary[400]};
            opacity: 1;
        }
    `}

    ${({ faint, theme }) => faint && css`
        color: ${theme.brand?.primary ?? primitiveColors.primary[500]};
        background-color: ${theme.brand?.primaryTransparent ?? primitiveColors.alpha.primary20};
    `}

    ${({ success, theme }) => success && css`
        background: ${theme.brand?.secondary ?? primitiveColors.secondary[500]};
        &:hover {
            background: ${theme.brand?.secondaryDark ?? primitiveColors.secondary[600]};
            opacity: 1;
        }
    `}

    ${({ danger, theme }) => danger && css`
        background: ${theme.brand?.error ?? primitiveColors.error[500]};
        color: ${primitiveColors.gray[50]};
        &:hover {
            background: ${theme.brand?.error ?? primitiveColors.error[500]};
            opacity: 0.85;
        }
    `}

    ${({ transparent, theme }) => transparent && css`
        background: transparent;
        color: ${theme.semantic.text.primary};
        &:hover {
            background: transparent;
        }
    `}

    ${({ theme, disabled }) => disabled && css`
        color: ${theme.semantic.text.secondary};
        &:active { transform: none; }
        &:hover { opacity: 1; color: ${theme.semantic.text.secondary}; }
        &:before, &:after, &:hover:before, &:hover:after {
            transition: none;
            transform: none;
            opacity: 1;
        }
    `}
`

const IconButton: React.FC<PropsType> = props => {
    const { children, href, ...restProps } = props;

    if (href) {
        return <Wrap as="a" href={href} {...restProps}>{children}</Wrap>;
    }

    return <Wrap {...restProps}>{children}</Wrap>;
}

export default IconButton;